import * as THREE from 'three'
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js'
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js'
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js'

import AudioMaterial from './AudioMaterial.js'
import CompositeMaterial from './CompositeMaterial.js'

/**
 * This class manages the THREE JS lifecycle and provides hooks and methods to
 * interact with it in a limited manner from the main program.
 *
 * Internally, this class stores a list of each mesh that needs to be rendered
 * for the scene (one per audio sample) along with a WebGLRenderTarget for each.
 * On each call to the #animate function, each mesh is rendered to it's own
 * render target. Each render is then passed through the EffectComposer pass,
 * which starts by blending the earlier passes together, drawing the result to
 * a simple quad, and rendering that scene with an orthographic camera.
 *
 * @param {Element} container - A parent container to append the renderer canvas
 * to. If nothing is supplied, the canvas is added to the document root.
 */
export default class SceneVisualizer {
  /**
   * Values which define the SceneVisualizer's current running state.
   * @type {Enum}
   */
  static State = {
    UNSTARTED: 0,
    STARTED: 1,
    PLAYING: 2,
    PAUSED: 3
  }

  /**
   * The SceneVisualizer running state.
   * @type {SceneVisualizer.State}
   */
  get state() {
    return this.#state
  }

  /**
   * A debugging helper that contains a human-readable copy of the current state
   * enum value.
   * @type {String}
   */
  get humanReadableState() {
    switch (this.#state) {
      case SceneVisualizer.State.UNSTARTED:
        return 'UNSTARTED'
      case SceneVisualizer.State.STARTED:
        return 'STARTED'
      case SceneVisualizer.State.PLAYING:
        return 'PLAYING'
      case SceneVisualizer.State.PAUSED:
        return 'PAUSED'
      default:
        return undefined
    }
  }

  /**
   * The containing element for the renderer.domElement canvas.
   * @type {Element}
   */
  #domContainer

  /**
   * A THREE WebGL renderer wrapper.
   * @type {THREE.WebGLRenderer}
   */
  #renderer

  /**
   * An EffectComposer, used to string together multiple renders into a final
   * image.
   * @type {THREE.EffectComposer}
   */
  #composer

  /**
   * Stores related sets of meshes and the render targets they'll be drawn to.
   * @type {Array<{object: THREE.Mesh, renderTarget: THREE.WebGLRenderTarget}>}
   */
  #subScenes = []

  // SCENE OBJECTS -------------------------------------------------------------

  /**
   * The single THREE JS scene used for the visualizer.
   * @type {THREE.Scene}
   */
  #scene

  /**
   * Outer object group used for scroll-based rotation.
   * @type {THREE.Group}
   */
  #interactiveGroup

  /**
   * Inner object group used for positioning.
   * @type {THREE.Group}
   */
  #objectGroup

  /**
   * A simple quad used to draw the composited sub-scenes to.
   * @type {THREE.Mesh}
   */
  #renderPlane

  /**
   * An array of THREE JS mesh objects.
   * @type {Array<THREE.Mesh>}
   */
  get objects() {
    return this.#subScenes.map(t => t.object)
  }

  // TIMER MANAGEMENT ----------------------------------------------------------

  /**
   * Stores a requestAnimationFrame ID.
   * @type {Number}
   */
  #rAF

  /**
   * Helps track elapsed time since the visualizer started.
   * @type {DOMHighResTimeStamp}
   */
  #previousTimestamp

  /**
   * Tracks the timestamp at the moment a call to pause() was processed.
   * @type {DOMHighResTimeStamp}
   */
  #pauseTimestamp

  /**
   * Time since the visualizer started.
   */
  #elapsedTime = 0

  /**
   * comment
   * unstarted | ready | playing | paused
   */
  #state = SceneVisualizer.State.UNSTARTED

  // SETUP ---------------------------------------------------------------------

  constructor(container) {
    const fov = 18

    this.#domContainer = container || document.body
    this.#scene = new THREE.Scene()
    this.#renderer = new THREE.WebGLRenderer({
      alpha: false,
      antialias: false,
      stencil: false,
      powerPreference: 'high-performance'
    })
    this.#composer = new EffectComposer(this.#renderer)

    this.#domContainer.appendChild(this.#renderer.domElement)

    // The perspective camera is reused to render each audio object individually
    this.camera = new THREE.PerspectiveCamera(
      fov,
      this.#domContainer.clientWidth / this.#domContainer.clientHeight,
      0.001,
      10
    )

    // The orthographic camera renders the renderPlane object, which fills the
    // entire screen and composites the previous renders together
    this.orthoCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1)

    // Add the renderPlane to the scene root. It's visibility is toggled during
    // the appropriate moments in the render loop
    this.#renderPlane = new THREE.Mesh(
      new THREE.PlaneGeometry(2, 2),
      new CompositeMaterial('Add')
    )
    this.#scene.add(this.#renderPlane)
    this.#renderPlane.visible = false

    this.#interactiveGroup = new THREE.Group()
    this.#objectGroup = new THREE.Group()

    this.#objectGroup.rotation.set(
      -64 * (Math.PI / 180),
      0,
      0
    )

    // Scene
    //  ↳ interactiveGroup
    //     ↳ objectGroup
    //        ↳ object
    //        ↳ object
    //        ↳ object
    this.#interactiveGroup.add(this.#objectGroup)
    this.#scene.add(this.#interactiveGroup)

    this.camera.position.z = 75 / fov
    this.camera.position.x = 2
    this.camera.position.set(
      0.838,
      0.326,
      4.545
    )
    this.camera.lookAt(0, 0.29, 0)

    // The render pass is the composite output of each individual object pass
    const renderPass = new RenderPass(this.#scene, this.orthoCamera)
    this.#composer.addPass(renderPass)

    // The render pass is then passed to a bloom filter
    this.bloomPass = new UnrealBloomPass()
    this.bloomPass.threshold = 0
    this.bloomPass.strength = 0.58
    this.bloomPass.radius = 0.19
    this.bloomPass.exposure = 0
    this.#composer.addPass(this.bloomPass)

    // The pipeline is finalized by sending to an OutputPass
    const outputPass = new OutputPass()
    this.#composer.addPass(outputPass)

    this.#updateSize()
    window.addEventListener('resize', this.#updateSize)

    this.#listenForDensityChange()

    this.#state = SceneVisualizer.State.STARTED
  }

  // RENDERER SIZE MANAGEMENT --------------------------------------------------

  /**
   * Listens for changes to devicePixelRatio and calls the updateSize method.
   *
   * There is no event or media query to listen to all changes to
   * devicePixelRatio, so what this method does is listen for a change away from
   * the current value. It updates a removeEventListener call so that each time
   * it fires it can remove the previous listener and register a new one.
   */
  #listenForDensityChange = () => {
    this.#removeListenForDensityChange()

    const query = matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`)
    query.addEventListener('change', this.#listenForDensityChange)
    this.#removeListenForDensityChange = () => {
      query.removeEventListener('change', this.#listenForDensityChange)
    }

    this.#updateSize()
  }

  /**
   * This is updated from listenForDensityChange to hold a removeEventListener
   * call for the current devicePixelRatio media query.
   *
   * It is initialized with an empty function call to not cause issues the first
   * time listenForDensityChange is called.
   */
  #removeListenForDensityChange = () => {}

  /**
   * Updates the camera aspect ratio, the renderer and composer sizes and pixel
   * ratios, and the sub-scene render target sizes.
   *
   * This is calculated using the size of #domContainer. It is up to other parts
   * of the program to manage the size of that element.
   *
   */
  #updateSize = () => {
    const { clientWidth: width, clientHeight: height } = this.#domContainer
    const ratio = Math.min(2, window.devicePixelRatio)

    this.camera.aspect = (width * 2) / height
    this.camera.updateProjectionMatrix()

    this.#renderer.setPixelRatio(ratio)
    this.#composer.setPixelRatio(ratio)

    this.#renderer.setSize(width, height, false)
    this.#composer.setSize(width, height, false)

    this.#subScenes.forEach(({ renderTarget }) => {
      renderTarget.setSize(width * ratio, height * ratio)
    })
  }

  // RENDERING -----------------------------------------------------------------

  /**
   * Animation loop for the scene renderer.
   *
   * @param {DOMHighResTimeStamp}
   *
   * Contains two hooks for which are called at the start and end of each render
   * frame: onBeforeRender and onAfterRender, respectively.
   *
   *   ┌────────────────────────────────┐       ┌────────────────────────┐
   *   │           SUB-SCENES           │┐      │    EFFECT COMPOSER     │
   *   │                                │├┐     │                        │
   *   │ ┌────── visibility TRUE        │││     │ ┌────────────────────┐ │
   *   │ │  ┌────────────────────────┐  │││  ┌───▶│     RenderPass     │ │
   *   │ ├─▶│  draw to renderTarget  │  │││  │  │ └────────────────────┘ │
   *   │ │  └────────────────────────┘  │││  │  │ ┌────────────────────┐ │
   *   │ ├─────▶ visibility FALSE       │││  │  │ │  UnrealBloomPass   │ │
   *   │ │  ┌────────────────────────┐  │││  │  │ └────────────────────┘ │
   *   │ │  │  pass to renderPlane   │  │││  │  │ ┌────────────────────┐ │
   *   │ └─▶│   uTextures uniforms   │  │││  │  │ │     OutputPass     │ │
   *   │    └────────────────────────┘  │││  │  │ └────────────────────┘ │
   *   └┬───────────────────────────────┘││  │  │            │           │
   *    └┬──────────────│────────────────┘│  │  │            ▼           │
   *     └──────────────│─────────────────┘  │  │     draw to screen     │
   *                    └────────────────────┘  └────────────────────────┘
   */
  #animate = (timestamp, once) => {
    if (once !== true) {
      this.#rAF = requestAnimationFrame(this.#animate)
    }

    if (this.#state !== SceneVisualizer.State.PLAYING) {
      cancelAnimationFrame(this.#rAF)
    }

    const timeDelta = timestamp - this.#previousTimestamp
    this.#elapsedTime += timeDelta
    this.#previousTimestamp = timestamp

    if (once !== true) {
      this.onBeforeRender(this, timestamp / 1000, this.#elapsedTime / 1000)
    }

    this.#subScenes.forEach(({ object, renderTarget }, index) => {
      object.visible = true
      this.#renderer.setRenderTarget(renderTarget)
      this.#renderer.render(this.#scene, this.camera)
      object.visible = false

      renderTarget.texture.needsUpdate = true

      this.#renderPlane.material.updateTexture(index, renderTarget.texture)
    })

    this.#renderPlane.visible = true
    this.#renderer.setRenderTarget(null)
    this.#composer.render(this.#scene, this.orthoCamera)
    this.#renderPlane.visible = false

    if (once !== true) {
      this.onAfterRender(this, timestamp / 1000, this.#elapsedTime / 1000)
    }
  }

  /**
   * Causes the render loop to run only once, without updating the timestamp.
   */
  #updateOnce = () => {
    this.#animate(document.timeline.currentTime, true)
  }

  // PUBLIC API ----------------------------------------------------------------

  /**
   * Starts the render loop.
   */
  run() {
    this.#state = SceneVisualizer.State.PLAYING
    this.#previousTimestamp = document.timeline.currentTime
    this.#animate(document.timeline.currentTime)
  }

  /**
   * Adds a new sub-scene object, corresponding to an audio track.
   * @param {Object} settings - Settings to be passed into the AudioMaterial
   * instance.
   */
  addObject(settings) {
    const geometry = new THREE.PlaneGeometry(
      12, // width
      3, // height
      200, // widthSegments
      90 // heightSegments
    )

    const object = new THREE.Mesh(
      geometry,
      new AudioMaterial({
        width: geometry.parameters.width,
        depth: geometry.parameters.height,
        ...settings
      })
    )
    object.visible = false

    if ('rotation' in settings) {
      object.rotation.set(
        settings.rotation * (Math.PI / 180),
        0,
        0
      )
    }

    this.#objectGroup.add(object)

    const renderTarget = new THREE.WebGLRenderTarget(
      this.#domContainer.clientWidth * window.devicePixelRatio,
      this.#domContainer.clientHeight * window.devicePixelRatio
    )
    renderTarget.format = THREE.RGBAFormat

    this.#subScenes.push({
      object: object,
      renderTarget: renderTarget
    })

    this.#renderPlane.material.addTexture(renderTarget.texture)
  }

  /**
   * Pauses the render loop.
   */
  pause() {
    cancelAnimationFrame(this.#rAF)
    this.#state = SceneVisualizer.State.PAUSED

    this.#pauseTimestamp = document.timeline.currentTime
    window.addEventListener('resize', this.#updateOnce)
  }

  /**
   * Unpauses the render loop.
   */
  unpause() {
    this.#state = SceneVisualizer.State.PLAYING
    this.#animate(this.#previousTimestamp)

    window.removeEventListener('resize', this.#updateOnce)
  }

  /**
   * Update the blend mode used for the underlying CompositeMaterial used by
   * the renderPlane.
   * @param {String} mode - The name of a blending mode, provided in /shaderlib/blending.glsl.js
   */
  setBlendingMode(mode) {
    this.#renderPlane.material.blendMode = mode
  }

  /**
   * Sets the rotation of the interactiveGroup.
   * @param {Number} angle - A value represented in degrees of rotation around
   * the x-axis.
   */
  setInteractiveRotation(angle) {
    this.#interactiveGroup.rotation.set(
      angle * (Math.PI / 180),
      0,
      0
    )
  }

  /**
   * Callback executed at the start of the render loop for each frame. It will
   * be passed two arguments: a reference to this instance of SceneVisualizer,
   * and a DOMHighResTimeStamp representing the time stamp of when the call was
   * executed.
   */
  onBeforeRender = () => {}

  /**
   * Callback executed at the end of the render loop for each frame. It will be
   * passed two arguments: a reference to this instance of SceneVisualizer, and
   * a DOMHighResTimeStamp representing the time stamp of when the call was
   * executed.
   */
  onAfterRender = () => {}
}
